import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getTeamTasks, createTeamTask, deleteTeamTask, toggleTaskCompletion } from '../firebase/teamService';
import { getTeamMessages, createTeamMessage, deleteTeamMessage } from '../firebase/teamService';
import { getUsers } from '../firebase/userService';
import { isAdmin, canDeleteAnyTask, canDeleteCompletedTask } from '../utils/permissions';
import './TeamDashboard.css';

function TeamDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);

  useEffect(() => {
    loadData();
  }, [currentUser]);

  const loadData = async () => {
    setLoading(true);

    // Load users first
    const usersResult = await getUsers();
    if (usersResult.success) {
      setUsers(usersResult.users);
      const userData = usersResult.users.find(u => u.email?.toLowerCase() === currentUser?.email?.toLowerCase());
      setCurrentUserData(userData);
    }

    // Load tasks and messages
    const tasksResult = await getTeamTasks();
    const messagesResult = await getTeamMessages();

    if (tasksResult.success) {
      setTasks(tasksResult.tasks);
    }
    if (messagesResult.success) {
      setMessages(messagesResult.messages);
    }

    setLoading(false);
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;

    const result = await createTeamTask({
      title: newTask,
      completed: false,
      createdBy: currentUser.uid,
      createdByName: currentUserData?.name || currentUser.email,
      createdByInitials: currentUserData?.initials || currentUser.email.substring(0, 2).toUpperCase()
    });

    if (result.success) {
      setNewTask('');
      loadData();
    }
  };

  const handleCreateMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageData = {
      content: newMessage,
      createdBy: currentUser.uid,
      createdByName: currentUserData?.name || currentUser.email,
      createdByInitials: currentUserData?.initials || currentUser.email.substring(0, 2).toUpperCase(),
      replyTo: replyingTo ? replyingTo.id : null,
      replyToContent: replyingTo ? replyingTo.content : null,
      replyToName: replyingTo ? replyingTo.createdByName : null
    };

    const result = await createTeamMessage(messageData);

    if (result.success) {
      setNewMessage('');
      setReplyingTo(null);
      loadData();
    }
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    const completedBy = !currentStatus ? {
      uid: currentUser.uid,
      name: currentUserData?.name || currentUser.email,
      initials: currentUserData?.initials || currentUser.email.substring(0, 2).toUpperCase()
    } : null;

    const result = await toggleTaskCompletion(taskId, !currentStatus, completedBy);
    if (result.success) {
      loadData();
    }
  };

  const handleDeleteTask = async (task) => {
    const userIsAdmin = isAdmin(currentUserData);
    const isOwnTask = task.createdBy === currentUser.uid;
    const isCompleted = task.completed;

    // Check permissions
    if (isCompleted && !canDeleteCompletedTask(currentUserData)) {
      alert('Only admins can delete completed tasks');
      return;
    }

    if (!isOwnTask && !canDeleteAnyTask(currentUserData)) {
      alert('You can only delete your own tasks');
      return;
    }

    if (window.confirm('Are you sure you want to delete this task?')) {
      const result = await deleteTeamTask(task.id);
      if (result.success) {
        loadData();
      }
    }
  };

  const handleDeleteMessage = async (message) => {
    const isOwnMessage = message.createdBy === currentUser.uid;
    const userIsAdmin = isAdmin(currentUserData);

    if (!isOwnMessage && !userIsAdmin) {
      alert('You can only delete your own messages');
      return;
    }

    if (window.confirm('Are you sure you want to delete this message?')) {
      const result = await deleteTeamMessage(message.id);
      if (result.success) {
        loadData();
      }
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Organize messages into threads
  const organizeMessageThreads = () => {
    const parentMessages = messages.filter(msg => !msg.replyTo);
    const replyMessages = messages.filter(msg => msg.replyTo);

    return parentMessages.map(parent => ({
      ...parent,
      replies: replyMessages.filter(reply => reply.replyTo === parent.id)
    }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="team-dashboard"><p>Loading...</p></div>;
  }

  return (
    <div className="team-dashboard">
      <div className="team-header">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Dashboard
        </button>
        <h1>Team Dashboard</h1>
      </div>

      <div className="team-content">
        {/* Tasks Section */}
        <div className="team-section tasks-section">
          <h2>📋 Team Tasks</h2>

          <form onSubmit={handleCreateTask} className="task-form">
            <input
              type="text"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder="Add a new task..."
              className="task-input"
            />
            <button type="submit" className="add-btn">+ Add</button>
          </form>

          <div className="team-tasks-list">
            {tasks.length === 0 ? (
              <p className="no-data">No tasks yet. Create one above!</p>
            ) : (
              tasks.map(task => (
                <div key={task.id} className={`task-item ${task.completed ? 'completed' : ''}`}>
                  <label className="task-checkbox-label">
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={task.completed}
                        onChange={() => handleToggleTask(task.id, task.completed)}
                      />
                      {task.completed && task.completedBy && (
                        <div className="completed-avatar" title={`Completed by ${task.completedBy.name}`}>
                          {task.completedBy.initials}
                        </div>
                      )}
                    </div>
                    <span className="task-title">{task.title}</span>
                  </label>

                  <div className="task-meta">
                    <span className="task-creator">by {task.createdByInitials}</span>
                    <button
                      onClick={() => handleDeleteTask(task)}
                      className="delete-task-btn"
                      title="Delete task"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Messages Section */}
        <div className="team-section messages-section">
          <h2>💬 Team Messages</h2>

          <div className="team-messages-list">
            {organizeMessageThreads().map(message => (
              <div key={message.id} className="message-thread">
                {/* Parent Message */}
                <div className={`message-item ${replyingTo?.id === message.id ? 'replying-to' : ''}`}>
                  {replyingTo?.id === message.id && (
                    <div className="replying-to-indicator">Replying to this message</div>
                  )}
                  <div className="message-header">
                    <div className="message-avatar">
                      {message.createdByInitials}
                    </div>
                    <div className="message-info">
                      <strong>{message.createdByName}</strong>
                      <span className="message-time">
                        {formatTimestamp(message.createdAt)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleReply(message)}
                      className="reply-message-btn"
                      title="Reply to message"
                    >
                      ↩
                    </button>
                    <button
                      onClick={() => handleDeleteMessage(message)}
                      className="delete-message-btn"
                      title="Delete message"
                    >
                      ×
                    </button>
                  </div>
                  <div className="message-content">
                    {message.content}
                  </div>
                </div>

                {/* Reply Messages */}
                {message.replies && message.replies.length > 0 && (
                  <div className="message-replies">
                    {message.replies.map(reply => (
                      <div key={reply.id} className="message-item message-reply">
                        <div className="message-header">
                          <div className="message-avatar message-avatar-small">
                            {reply.createdByInitials}
                          </div>
                          <div className="message-info">
                            <strong>{reply.createdByName}</strong>
                            <span className="message-time">
                              {formatTimestamp(reply.createdAt)}
                            </span>
                          </div>
                          <button
                            onClick={() => handleDeleteMessage(reply)}
                            className="delete-message-btn"
                            title="Delete message"
                          >
                            ×
                          </button>
                        </div>
                        <div className="message-content">
                          {reply.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleCreateMessage} className="message-form">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={replyingTo ? "Type your reply..." : "Type a message..."}
              className="message-input"
              rows="3"
            />
            <div className="message-form-actions">
              <button type="submit" className="send-btn">{replyingTo ? 'Reply' : 'Send'}</button>
              {replyingTo && (
                <button
                  type="button"
                  onClick={cancelReply}
                  className="cancel-reply-btn-inline"
                  title="Cancel reply"
                >
                  Cancel Reply
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TeamDashboard;
